import React, { useState, useCallback, useEffect, useRef } from "react";
import { HexColorPicker } from "react-colorful";
import { Box, Popover, TextField } from "@material-ui/core";
import { normalizeHexInput } from "./colorUtils";

const isWhite = (h) => {
	const s = (h || "").toLowerCase().replace(/^#/, "");
	return s === "fff" || s === "ffffff";
};

export default function HexColorPickerPopover({ value, onChange, label }) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [hexInput, setHexInput] = useState("");
	const popoverContentRef = useRef(null);
	const hex = value && /^#([0-9A-Fa-f]{3}){1,2}$/.test(value) ? value : "#ffffff";

	const [popoverHex, setPopoverHex] = useState("");
	const handleOpen = (e) => {
		setAnchorEl(e.currentTarget);
		setHexInput(hex);
		setPopoverHex(hex);
	};
	const handleClose = useCallback(() => setAnchorEl(null), []);

	useEffect(() => {
		if (!anchorEl) return;
		const onDocMouseDown = (e) => {
			const t = e.target;
			if (anchorEl && typeof anchorEl.contains === "function" && anchorEl.contains(t)) return;
			const root = popoverContentRef.current;
			if (root && root.contains(t)) return;
			handleClose();
		};
		document.addEventListener("mousedown", onDocMouseDown, true);
		return () => document.removeEventListener("mousedown", onDocMouseDown, true);
	}, [anchorEl, handleClose]);

	const handleHexInputChange = useCallback((e) => {
		const v = e.target.value;
		setHexInput(v);
		const normalized = normalizeHexInput(v);
		if (normalized) onChange(normalized);
	}, [onChange]);

	const handleHexInputBlur = useCallback(() => {
		const normalized = normalizeHexInput(hexInput);
		if (normalized) {
			onChange(normalized);
			setHexInput(normalized);
		} else {
			setHexInput("");
		}
	}, [hexInput, onChange]);

	useEffect(() => {
		setHexInput("");
	}, [hex]);
	const displayValue = hexInput !== "" ? hexInput : hex;

	return (
		<Box>
			{label && (
				<Box fontSize={12} fontWeight={600} color="#64748b" mb={0.75}>
					{label}
				</Box>
			)}
			<Box display="flex" alignItems="center" style={{ gap: 8 }}>
				<Box
					onClick={handleOpen}
					title="Abrir selector de color"
					style={{
						width: 36,
						height: 36,
						borderRadius: "50%",
						border: "2px solid #e2e8f0",
						backgroundColor: hex,
						cursor: "pointer",
						boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
						backgroundImage: isWhite(hex)
							? "linear-gradient(45deg, #e2e8f0 25%, transparent 25%), linear-gradient(-45deg, #e2e8f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e2e8f0 75%), linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)"
							: undefined,
						backgroundSize: isWhite(hex) ? "8px 8px" : undefined,
						backgroundPosition: isWhite(hex) ? "0 0, 0 4px, 4px -4px, -4px 0" : undefined,
						flexShrink: 0,
					}}
				/>
				<TextField
					size="small"
					placeholder="#HEX"
					value={displayValue}
					onChange={handleHexInputChange}
					onBlur={handleHexInputBlur}
					onFocus={() => setHexInput(hex)}
					inputProps={{ style: { fontFamily: "monospace", fontSize: 13 }, maxLength: 9 }}
					style={{ minWidth: 88 }}
				/>
			</Box>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ style: { borderRadius: 12, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.12)" } }}
			>
				<Box ref={popoverContentRef} p={1.5}>
					<Box
						style={{
							height: 120,
							width: 160,
							borderRadius: 10,
							overflow: "hidden",
						}}
					>
						<HexColorPicker color={hex} onChange={(c) => { onChange(c); setPopoverHex(c); }} style={{ width: "100%", height: "100%" }} />
					</Box>
					<TextField
						size="small"
						placeholder="#HEX"
						value={popoverHex}
						onChange={(e) => {
							const v = e.target.value;
							setPopoverHex(v);
							const normalized = normalizeHexInput(v);
							if (normalized) onChange(normalized);
						}}
						onBlur={() => setPopoverHex(hex)}
						inputProps={{ style: { fontFamily: "monospace", fontSize: 12 }, maxLength: 9 }}
						style={{ width: "100%", marginTop: 8 }}
					/>
				</Box>
			</Popover>
		</Box>
	);
}
